/* ─────────────────────────────────────────────
   DEVICE PANEL — device header + device/teammate panel (Cluster 12)
   renderDevicePanel, openDeviceConsole, VIS_OPTIONS,
   COHORT_PRECISION_OPTIONS, COHORT_RANGE_OPTIONS
   ───────────────────────────────────────────── */
import { $ } from './dom';
import { deviceDisplayName, isHighEntropyDevice } from './animals';
import { getAuthUserId, getMyClaims, getConsoleTarget, setConsoleTarget } from '../state/auth-state';
import { MOCK } from '../state/device';
import { state } from '../state/app-state';
import { loadKeypair } from '../storage/persistence';
import { joinCode } from '../../shared/highentropy';
import {
  startCommandConsole, renderCommandControls, renderCommandFeed, sendConsoleCommand,
  renderTeammateConsole,
} from './command-console';
import {
  currentGroupPath, currentGroupClaimState, groupShareBody,
  claimCurrentGroup, revokeCurrentGroupClaim, mintInviteForCurrentGroup,
  openGroupConsole, probeGroupClaim,
} from './group-claim';

// Callbacks wired from main.ts to avoid circular imports
// (device-panel → main is not allowed; main wires these at module-eval time).
let _openDrawerDeviceCb: (() => void) | null = null;
export function setOpenDrawerDeviceCb(fn: () => void): void { _openDrawerDeviceCb = fn; }
let _openShareModalCb: (() => void) | null = null;
export function setOpenShareModalCb(fn: () => void): void { _openShareModalCb = fn; }
let _renameCurrentDeviceCb: (() => void) | null = null;
export function setRenameCurrentDeviceCb(fn: () => void): void { _renameCurrentDeviceCb = fn; }
let _claimCurrentDeviceCb: (() => void) | null = null;
export function setClaimCurrentDeviceCb(fn: () => void): void { _claimCurrentDeviceCb = fn; }
let _openBackupExportCb: (() => void) | null = null;
export function setOpenBackupExportCb(fn: () => void): void { _openBackupExportCb = fn; }
let _unclaimCurrentDeviceCb: (() => void) | null = null;
export function setUnclaimCurrentDeviceCb(fn: () => void): void { _unclaimCurrentDeviceCb = fn; }
let _changeVisibilityCb: ((v: string) => void) | null = null;
export function setChangeVisibilityCb(fn: (v: string) => void): void { _changeVisibilityCb = fn; }
let _changeCohortScopeCb: (() => void) | null = null;
export function setChangeCohortScopeCb(fn: () => void): void { _changeCohortScopeCb = fn; }
let _copyTextCb: ((text: string) => void) | null = null;
export function setCopyTextCb(fn: (text: string) => void): void { _copyTextCb = fn; }
let _forgetCurrentDeviceCb: (() => void) | null = null;
export function setForgetCurrentDeviceCb(fn: () => void): void { _forgetCurrentDeviceCb = fn; }

export const VIS_OPTIONS = ['public', 'unlisted', 'private', 'group'];
// Per-device cohort scope choices (Phase 2). Values are the wire enum
// (functions/_shared/shares.ts isRange/isPrecision); labels are human copy.
export const COHORT_PRECISION_OPTIONS = [
  { v: 'exact', l: 'Exact' },
  { v: '1km', l: '~1 km grid' },
  { v: 'city', l: '~10 km grid' },
];
export const COHORT_RANGE_OPTIONS = [
  { v: 'all', l: 'All time' },
  { v: '7d', l: 'Last 7 days' },
  { v: '24h', l: 'Last 24 hours' },
];

// Open the device console targeting a specific token. Local device → the full
// management panel (consoleTarget cleared); a teammate → a slim read-only console.
export function openDeviceConsole(token: string, isLocal: boolean){
  setConsoleTarget(isLocal ? null : { token, canIssue: !!getMyClaims()?.has(token) });
  _openDrawerDeviceCb?.();
}

export function renderDevicePanel(){
  const box = $('device-box');
  if(!box) return;
  // Phase 3: an explicit NON-local console target (a teammate's device opened
  // from a map pin) renders a slim read-only console — never the local-device
  // management panel (claim / visibility / forget are nonsensical for someone
  // else's device). The local device (consoleTarget == null) keeps the full panel.
  if(getConsoleTarget() && getConsoleTarget()!.token !== MOCK.device?.token){
    renderTeammateConsole(box, getConsoleTarget()!.token);
    return;
  }
  if(!MOCK.device){ box.innerHTML = `<div class="acct-hint">No active device.</div>`; return; }
  const d = MOCK.device;
  const names = deviceDisplayName(d.token, d.animals, d.group, undefined, d.tag);
  // Display tag (ADR 0022): a FREE feature gated only by key possession (no
  // claim/sign-in), so the control is top-level and offered whenever this
  // browser holds the device's signing key — not inside the claim block.
  const canTag = !!loadKeypair(d.token);
  const tagBtn = canTag
    ? `<button class="menu-action" id="device-tag-btn"><span>${d.tag ? 'Rename device…' : 'Set a display name…'}</span><i class="ph ph-pencil-simple"></i></button>`
    : '';
  let claimControls: string;
  if(getAuthUserId() == null){
    claimControls = `<div class="acct-hint">Sign in (Account) to claim this device and set its visibility.</div>`;
  } else {
    const claim = getMyClaims()?.get(d.token);
    if(!claim){
      claimControls = `<button class="menu-action" id="claim-btn"><span>Claim this device</span><i class="ph ph-anchor"></i></button>`;
    } else {
      const vis = claim.visibility || 'public';
      const opts = VIS_OPTIONS.map(v =>
        `<option value="${v}"${v === vis ? ' selected' : ''}>${v[0].toUpperCase() + v.slice(1)}</option>`).join('');
      // Per-device cohort scope (Phase 2). Only meaningful — and only offered —
      // for a `group`-visibility device, the one that actually appears (coarsened)
      // in the semi-public cohort. The current value is the scope the server last
      // echoed for this device; defaults to {all, exact}.
      const curScope = state.cohort.get(d.token)?.cohort_scope ?? { range: 'all', precision: 'exact' };
      const precOpts = COHORT_PRECISION_OPTIONS.map(o =>
        `<option value="${o.v}"${o.v === curScope.precision ? ' selected' : ''}>${o.l}</option>`).join('');
      const rangeOpts = COHORT_RANGE_OPTIONS.map(o =>
        `<option value="${o.v}"${o.v === curScope.range ? ' selected' : ''}>${o.l}</option>`).join('');
      const scopeControls = vis === 'group'
        ? `<div class="acct-hint">Cohort precision &amp; range control how this <b>group</b> device appears to others in the cohort. Your own map always stays exact.</div>` +
          `<div class="menu-row"><span class="k">Cohort precision</span><select id="cohort-precision-select" class="vis-select">${precOpts}</select></div>` +
          `<div class="menu-row"><span class="k">Cohort range</span><select id="cohort-range-select" class="vis-select">${rangeOpts}</select></div>`
        : '';
      // Remote console (ADR 0024): a capability-scoped dropdown + live feed for
      // the owner. The dropdown options + enable/disable are filled by
      // renderCommandControls() once the first poll resolves capabilities.
      const remoteConsole =
        `<hr class="drawer-div"/><div class="menu-label">Remote console</div>` +
        `<div class="acct-hint" id="cmd-hint">Loading the device's advertised queries…</div>` +
        `<div class="menu-row"><select id="cmd-kind-select" class="vis-select" disabled></select>` +
        `<button class="menu-action cmd-send" id="cmd-send-btn" disabled><span>Send</span><i class="ph ph-paper-plane-tilt"></i></button></div>` +
        `<div class="cmd-feed" id="cmd-feed"><div class="acct-hint">Loading…</div></div>`;
      // Group sharing (ADR 0025): only meaningful for a grouped device, and now
      // STATEFUL — the button set reflects the actual claim state (unclaimed /
      // owner / participant), resolved from the live console role or a one-shot
      // probe (groupShareBody). Claiming activates shared C2; invites gate new
      // device creation into it.
      const gp = currentGroupPath();
      const groupShare = (claim.group && claim.group.length === 2 && gp)
        ? `<hr class="drawer-div"/><div class="menu-label">Group sharing (TAK)</div>` +
          groupShareBody(currentGroupClaimState(gp))
        : '';
      claimControls =
        `<div class="menu-row"><span class="k">Visibility</span>` +
        `<select id="visibility-select" class="vis-select">${opts}</select></div>` +
        `<div class="acct-hint">A <b>public</b> device (the default) shows its live location to any signed-in user on the world map. Choose <b>private</b>, <b>unlisted</b>, or <b>group</b> to keep it off the world map.</div>` +
        scopeControls +
        remoteConsole +
        groupShare +
        `<hr class="drawer-div"/>` +
        `<button class="menu-action" id="backup-btn"><span>Back up this identity…</span><i class="ph ph-cloud-arrow-up"></i></button>` +
        `<button class="menu-action" id="unclaim-btn"><span>Un-claim this device</span><i class="ph ph-link-break"></i></button>`;
    }
  }
  // A high-entropy grouped device's group key is its private-cohort invite
  // secret. Surface a copyable join link; the group key is a password, not a
  // pair of animals you can say aloud.
  const code = (d.group && d.group.length === 2 && isHighEntropyDevice(d.animals)) ? joinCode(d.group) : null;
  const inviteBlock = code
    ? `<hr class="drawer-div"/><div class="menu-label">Private group invite</div>` +
      `<div class="acct-hint">Anyone with this key can join the group and see the cohort — treat it like a password.</div>` +
      `<button class="menu-action" id="invite-copy-btn"><span>Copy join link</span><i class="ph ph-link"></i></button>`
    : '';
  box.innerHTML =
    `<div class="device-card"><div class="da-name">${names}</div><div class="da-tail">${d.token}</div></div>` +
    `<button class="menu-action" id="device-share-btn"><span>Share…</span><i class="ph ph-share-network"></i></button>` +
    tagBtn +
    `<hr class="drawer-div"/>` + claimControls + inviteBlock +
    `<hr class="drawer-div"/>` +
    `<button class="menu-action danger" id="device-forget-btn"><span>Forget this device</span><i class="ph ph-trash"></i></button>`;
  $('device-share-btn')?.addEventListener('click', () => { _openShareModalCb?.(); });
  $('device-tag-btn')?.addEventListener('click', () => { _renameCurrentDeviceCb?.(); });
  $('claim-btn')?.addEventListener('click', () => { _claimCurrentDeviceCb?.(); });
  $('backup-btn')?.addEventListener('click', () => { _openBackupExportCb?.(); });
  $('unclaim-btn')?.addEventListener('click', () => { _unclaimCurrentDeviceCb?.(); });
  $('visibility-select')?.addEventListener('change', (e: any) => _changeVisibilityCb?.(e.target.value));
  $('cohort-precision-select')?.addEventListener('change', () => { _changeCohortScopeCb?.(); });
  $('cohort-range-select')?.addEventListener('change', () => { _changeCohortScopeCb?.(); });
  if(code) $('invite-copy-btn')?.addEventListener('click', () => _copyTextCb?.(`${location.origin}/#/join/${code}`));
  $('device-forget-btn')?.addEventListener('click', () => { _forgetCurrentDeviceCb?.(); });
  // Remote console: wire the send button + (re)start the poll for this device.
  // startCommandConsole first (it clears any stale cache for a *new* token, or
  // keeps the cache for the *same* token across a re-render), then paint
  // immediately from whatever cache survives so a re-render doesn't flash
  // "Loading…" for a poll cycle.
  if($('cmd-feed') && MOCK.device){
    $('cmd-send-btn')?.addEventListener('click', sendConsoleCommand);
    startCommandConsole(MOCK.device.token);
    renderCommandControls();
    renderCommandFeed();
  }
  // Group sharing affordances. Buttons are rendered conditionally by claim state,
  // so these bindings no-op when an id is absent.
  $('group-claim-btn')?.addEventListener('click', claimCurrentGroup);
  $('group-revoke-btn')?.addEventListener('click', revokeCurrentGroupClaim);
  $('group-invite-btn')?.addEventListener('click', mintInviteForCurrentGroup);
  $('group-console-btn')?.addEventListener('click', openGroupConsole);
  // If the claim state is still unknown ('checking'), probe once; probeGroupClaim
  // re-renders this panel via refreshAuthUi() when it resolves.
  { const gpc = currentGroupPath(); if(gpc && currentGroupClaimState(gpc) === 'checking') probeGroupClaim(gpc); }
}
